import { useState } from 'react'
import { PhotoIcon } from '@heroicons/react/20/solid'
import { CTA_LABELS, type AdCreative } from '../../lib/adPromotion'

function hostname(url?: string) {
  if (!url) return ''
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

function initials(name?: string) {
  const trimmed = (name ?? '').trim()
  if (!trimmed) return '?'
  const words = trimmed.split(/\s+/)
  return (words.length >= 2 ? words[0][0] + words[1][0] : trimmed.slice(0, 2)).toUpperCase()
}

type Props = {
  creative: AdCreative
  imageUrl?: string
  brandName?: string
  brandWebsite?: string
}

export default function MetaAdPreview({ creative, imageUrl, brandName, brandWebsite }: Props) {
  const [faviconFailed, setFaviconFailed] = useState(false)
  const domain = hostname(brandWebsite)
  const favicon = domain && !faviconFailed ? `https://www.google.com/s2/favicons?domain=${domain}&sz=64` : null

  return (
    <div className="flex h-full w-full max-w-[300px] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white">
      <div className="flex items-center gap-2 px-3 py-2.5">
        {favicon ? (
          <img
            src={favicon}
            alt=""
            onError={() => setFaviconFailed(true)}
            className="size-8 shrink-0 rounded-full bg-gray-200 object-cover"
          />
        ) : (
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-100">
            <span className="text-xs font-bold text-purple-700">{initials(brandName)}</span>
          </div>
        )}
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-gray-900">{brandName || 'Your Brand'}</p>
          <p className="text-[10px] text-gray-400">Sponsored</p>
        </div>
      </div>

      {creative.primaryText && (
        <div className="px-3 pb-2">
          <p className="line-clamp-3 text-xs text-gray-700">{creative.primaryText}</p>
        </div>
      )}

      <div className="min-h-0 w-full flex-1 overflow-hidden bg-gray-100">
        {imageUrl ? (
          <img src={imageUrl} alt="Ad preview" className="size-full object-cover" />
        ) : (
          <div className="flex size-full min-h-32 items-center justify-center">
            <PhotoIcon className="size-10 text-gray-300" />
          </div>
        )}
      </div>

      <div className="bg-gray-50 px-3 py-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            {creative.ctaLink && <p className="truncate text-[10px] text-gray-400 uppercase">{hostname(creative.ctaLink)}</p>}
            {creative.headline && <p className="truncate text-xs font-semibold text-gray-900">{creative.headline}</p>}
            {creative.description && <p className="truncate text-[10px] text-gray-500">{creative.description}</p>}
          </div>
          <button
            type="button"
            className="shrink-0 rounded bg-gray-200 px-3 py-1.5 text-[10px] font-semibold whitespace-nowrap text-gray-700 transition hover:bg-gray-300"
          >
            {CTA_LABELS[creative.ctaType]}
          </button>
        </div>
      </div>
    </div>
  )
}
